import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { usePageContent, getSection } from "@/hooks/usePageContent";
import SEO from "@/components/SEO";
import CountryFlag from "@/components/CountryFlag";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowRight, 
  GraduationCap, 
  Users, 
  Briefcase, 
  DollarSign,
  ChevronRight,
  Globe,
  TrendingUp,
  Sparkles
} from "lucide-react";

// Destination data - sorted alphabetically
const destinations = [
  {
    id: 'australia',
    name: 'Australia',
    shortName: 'Australia',
    flag: '🇦🇺',
    image: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=800',
    description: 'World-class universities in a stunning natural environment',
    stats: {
      universities: '43',
      intlStudents: '750K+',
      workVisa: '2-3 Years',
      avgCost: 'AUD 25-45k/yr'
    },
    highlights: ['Melbourne', 'Sydney', 'ANU'],
    popularPrograms: ['Engineering', 'Hospitality', 'Environmental Science'],
    color: 'from-yellow-500 to-green-600'
  },
  {
    id: 'canada',
    name: 'Canada',
    shortName: 'Canada',
    flag: '🇨🇦',
    image: 'https://images.unsplash.com/photo-1517935706615-2717063c2225?w=800',
    description: 'Quality education with excellent immigration pathways',
    stats: {
      universities: '100+',
      intlStudents: '800K+',
      workVisa: '3 Years PGWP',
      avgCost: 'CAD 20-40k/yr'
    },
    highlights: ['Toronto', 'UBC', 'McGill'],
    popularPrograms: ['Data Science', 'Healthcare', 'Business'],
    color: 'from-red-600 to-red-400'
  },
  {
    id: 'dubai',
    name: 'Dubai',
    shortName: 'Dubai',
    flag: '🇦🇪',
    image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800',
    description: 'Global business hub with international branch campuses and tax-free work opportunities',
    stats: {
      universities: '65+',
      intlStudents: '80K+',
      workVisa: '2 Years',
      avgCost: 'AED 40-90k/yr'
    },
    highlights: ['Heriot-Watt', 'Middlesex', 'BITS Pilani'],
    popularPrograms: ['Business', 'Hospitality', 'Engineering'],
    color: 'from-red-600 to-green-600'
  },
  {
    id: 'france',
    name: 'France',
    shortName: 'France',
    flag: '🇫🇷',
    image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800',
    description: 'Excellence in art, culture & world-class business schools',
    stats: {
      universities: '3500+',
      intlStudents: '370K+',
      workVisa: '2 Years',
      avgCost: '€3-20k/yr'
    },
    highlights: ['HEC Paris', 'Sorbonne', 'INSEAD'],
    popularPrograms: ['Business', 'Fashion', 'Culinary Arts'],
    color: 'from-blue-600 to-red-500'
  },
  {
    id: 'germany',
    name: 'Germany',
    shortName: 'Germany',
    flag: '🇩🇪',
    image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800',
    description: 'Free/low-cost education at top-ranked technical universities',
    stats: {
      universities: '400+',
      intlStudents: '400K+',
      workVisa: '18 Months',
      avgCost: '€0-20k/yr'
    },
    highlights: ['TU Munich', 'Heidelberg', 'LMU'],
    popularPrograms: ['Engineering', 'Automotive', 'Research'],
    color: 'from-black to-yellow-500'
  },
  {
    id: 'ireland',
    name: 'Ireland',
    shortName: 'Ireland',
    flag: '🇮🇪',
    image: 'https://images.unsplash.com/photo-1590089415225-401ed6f9db8e?w=800',
    description: 'Europe\'s tech hub with excellent post-study work options',
    stats: {
      universities: '34',
      intlStudents: '35K+',
      workVisa: '2 Years',
      avgCost: '€10-25k/yr'
    },
    highlights: ['Trinity', 'UCD', 'DCU'],
    popularPrograms: ['Computer Science', 'Pharma', 'Data Analytics'],
    color: 'from-green-600 to-orange-500'
  },
  {
    id: 'malaysia',
    name: 'Malaysia',
    shortName: 'Malaysia',
    flag: '🇲🇾',
    image: 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=800',
    description: 'Affordable quality education with international campuses',
    stats: {
      universities: '170+',
      intlStudents: '130K+',
      workVisa: 'Part-time Work',
      avgCost: 'MYR 15-50k/yr'
    },
    highlights: ['Monash MY', 'Nottingham MY', 'UM'],
    popularPrograms: ['Business', 'Engineering', 'Hospitality'],
    color: 'from-blue-700 to-yellow-500'
  },
  {
    id: 'new-zealand',
    name: 'New Zealand',
    shortName: 'New Zealand',
    flag: '🇳🇿',
    image: 'https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=800',
    description: 'Safe, welcoming country with innovative education system',
    stats: {
      universities: '8',
      intlStudents: '100K+',
      workVisa: '3 Years',
      avgCost: 'NZD 22-35k/yr'
    },
    highlights: ['Auckland', 'Otago', 'Victoria'],
    popularPrograms: ['Agriculture', 'Film', 'Tourism'],
    color: 'from-black to-blue-600'
  },
  {
    id: 'singapore',
    name: 'Singapore',
    shortName: 'Singapore',
    flag: '🇸🇬',
    image: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800',
    description: 'Asia\'s premier education hub with world-class universities',
    stats: {
      universities: '34',
      intlStudents: '75K+',
      workVisa: '1 Year',
      avgCost: 'SGD 30-60k/yr'
    },
    highlights: ['NUS', 'NTU', 'SMU'],
    popularPrograms: ['Computer Science', 'Business', 'Finance'],
    color: 'from-red-600 to-white'
  },
  {
    id: 'uk',
    name: 'United Kingdom',
    shortName: 'UK',
    flag: '🇬🇧',
    image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800',
    description: 'Experience world-class education with rich cultural heritage',
    stats: {
      universities: '160+',
      intlStudents: '600K+',
      workVisa: '2 Years PSW',
      avgCost: '£15-35k/yr'
    },
    highlights: ['Oxford', 'Cambridge', 'Imperial'],
    popularPrograms: ['Finance', 'Law', 'Medicine'],
    color: 'from-blue-700 to-red-600'
  },
  {
    id: 'usa',
    name: 'United States',
    shortName: 'USA',
    flag: '🇺🇸',
    image: 'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=800',
    description: 'Home to world-renowned universities and endless opportunities',
    stats: {
      universities: '4,000+',
      intlStudents: '1M+',
      workVisa: '3 Years OPT',
      avgCost: '$25-55k/yr'
    },
    highlights: ['MIT', 'Stanford', 'Harvard'],
    popularPrograms: ['Computer Science', 'Business', 'Engineering'],
    color: 'from-blue-600 to-red-500'
  }
];

// Quick comparison stats
const globalStats = [
  { label: 'Partner Universities', value: '500+', icon: GraduationCap },
  { label: 'Students Placed', value: '30,000+', icon: Users },
  { label: 'Visa Success Rate', value: '98%', icon: TrendingUp },
  { label: 'Countries', value: '15', icon: Globe },
];

export const DestinationsListPage = () => {
  const { isDark } = useTheme();
  
  // Fetch CMS content for the Destinations page
  const { content: cmsContent } = usePageContent('destinations-list');
  
  // Get CMS-managed content with fallbacks
  const heroContent = getSection(cmsContent, 'hero', {
    badge_text: 'Study Destinations',
    title: 'Your Gateway to',
    title_highlight: 'Global Education',
    subtitle: 'Explore top study destinations worldwide. Compare universities, costs, and opportunities to find your perfect match.'
  });
  
  const seoContent = getSection(cmsContent, 'seo', {
    title: 'Study Abroad Destinations',
    description: 'Explore top study destinations: USA, UK, Canada, Australia, Germany & more. Compare universities, living costs, visa requirements & job prospects for international students.',
    keywords: 'study abroad destinations, USA universities, UK universities, Canada study visa, Australia student visa, Germany free education, study in Europe'
  });
  
  // Get CMS-managed global stats and destinations with fallbacks
  const cmsGlobalStats = getSection(cmsContent, 'global_stats', null);
  const globalStatsContent = cmsGlobalStats && Array.isArray(cmsGlobalStats) && cmsGlobalStats.length > 0 
    ? cmsGlobalStats 
    : globalStats;
  
  const cmsDestinations = getSection(cmsContent, 'destinations_data', null);
  const destinationsContent = cmsDestinations && Array.isArray(cmsDestinations) && cmsDestinations.length > 0 
    ? cmsDestinations 
    : destinations;
  
  return (
    <>
      <SEO 
        title={seoContent.title}
        description={seoContent.description}
        keywords={seoContent.keywords}
      />
      <div className={`min-h-screen pt-16 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`} data-testid="destinations-list-page">
      {/* Hero Section - Cinematic Full-bleed */}
      <section className="py-20 md:py-32 relative overflow-hidden min-h-[70vh] flex items-center">
        {/* Background image - Travel/Adventure */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/4881148/pexels-photo-4881148.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Young travelers exploring the world"
            className="w-full h-full object-cover"
          />
          {/* Cinematic gradient overlay */}
          <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-[#1a2830]/95 via-[#233842]/70 to-transparent' : 'bg-gradient-to-r from-ux-charcoal/95 via-ux-charcoal/70 to-ux-blue-dark/50'}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-white/10 text-white border-white/20 mb-6 backdrop-blur-sm">
              {heroContent.badge_text}
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white tracking-tight">
              {heroContent.title} <span className="gradient-text-aurora">{heroContent.title_highlight}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed">
              {heroContent.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-ux-cyan to-ux-violet text-white hover:opacity-90 rounded-full px-8"
                asChild
              >
                <Link to="/contact" data-testid="hero-cta-primary">
                  <Sparkles className="mr-2 h-5 w-5" />
                  Find My Dream University
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Global Stats */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-ux-violet via-ux-cyan to-ux-blue" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            {globalStatsContent.map((stat) => {
              // Handle both CMS format (icon as string) and hardcoded format (icon as component)
              const IconComponent = typeof stat.icon === 'string' 
                ? { GraduationCap, Users, TrendingUp, Globe }[stat.icon] || Globe
                : stat.icon || Globe;
              return (
              <div key={stat.label} className="flex flex-col items-center">
                <IconComponent className="h-6 w-6 mb-2 opacity-80" />
                <p className="text-2xl md:text-3xl font-bold">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className={`py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className={`mb-4 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
              Choose Your Destination
            </Badge>
            <h2 className={`text-3xl md:text-5xl font-bold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
              Popular Study <span className="gradient-text-electric">Destinations</span>
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-white/60' : 'text-ux-charcoal/70'}`}>
              Each destination offers unique opportunities. Explore and compare to find 
              the best fit for your academic goals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {destinationsContent.map((destination) => (
              <Link 
                key={destination.id}
                to={`/destinations/${destination.id}`}
                className="group"
                data-testid={`destination-card-${destination.id}`}
              >
                <div className={`h-full overflow-hidden rounded-3xl glass-panel hover:scale-[1.02] transition-all duration-300`}>
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={destination.image}
                      alt={`Study in ${destination.name}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* Flag & Name Overlay */}
                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                      <CountryFlag country={destination.name} emoji={destination.flag} size="lg" />
                      <div>
                        <h3 className="text-2xl font-bold text-white">{destination.name}</h3>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className={`mb-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                      {destination.description}
                    </p>
                    
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-ux-cyan" />
                        <div>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/60'}`}>Universities</p>
                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{destination.stats.universities}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-ux-violet" />
                        <div>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/60'}`}>Intl Students</p>
                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{destination.stats.intlStudents}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-ux-coral" />
                        <div>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/60'}`}>Work Visa</p>
                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{destination.stats.workVisa}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-ux-amber" />
                        <div>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/60'}`}>Avg Cost</p>
                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{destination.stats.avgCost}</p>
                        </div>
                      </div>
                    </div>

                    {/* Popular Programs */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {destination.popularPrograms.map((program) => (
                        <Badge key={program} variant="secondary" className={`text-xs ${isDark ? 'bg-white/10 text-white/80' : ''}`}>
                          {program}
                        </Badge>
                      ))}
                    </div>

                    {/* Explore Link */}
                    <div className="flex items-center text-ux-cyan font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Explore {destination.shortName} <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-ux-blue via-ux-violet to-ux-cyan" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Not Sure Which Country is Right for You?
          </h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
            Our AI-powered university finder analyzes your profile and preferences to recommend the perfect destination.
          </p>
          
          <Button 
            size="lg"
            className="bg-white text-ux-blue rounded-full px-8 font-bold hover:bg-white/90"
            asChild
          >
            <Link to="/contact">
              <Sparkles className="w-5 h-5 mr-2" />
              Get Personalized Recommendations
            </Link>
          </Button>
        </div>
      </section>
    </div>
    </>
  );
};


export default DestinationsListPage;
